import { z } from "zod";

export const channelSchema = z.enum(["web", "ait", "ios", "android"]);
export const playabilitySchema = z.enum(["playable_safe", "adapted_safe", "reference_only", "safety_blocked"]);
export const safeOutcomeSchema = z.enum(["team_point", "conversation", "rest", "pass"]);
export const roomStatusSchema = z.enum(["lobby", "ready", "playing", "reveal", "rest", "ended", "expired"]);
export const venueIntents = [
  "local_pub", "first_round", "second_round", "group", "date", "solo", "outdoor", "late_night",
  "korean_pub", "pocha", "old_school", "izakaya", "wine_bar", "cocktail_bar", "craft_beer",
  "meat", "gopchang", "seafood", "spicy", "soup", "chinese", "japanese", "fried_chicken", "pizza", "cheese",
  "clear_soup", "spicy_soup", "gukbap", "bean_sprout", "cold_noodle", "noodle", "porridge", "burger",
] as const;
export const venueIntentSchema = z.enum(venueIntents);
export const placeSearchQuerySchema = z.object({
  lat: z.coerce.number().finite().min(-90).max(90),
  lon: z.coerce.number().finite().min(-180).max(180),
  intent: venueIntentSchema,
  radius: z.coerce.number().int().min(300).max(2_000).default(1_200),
  locationMode: z.enum(["hub", "current"]).default("current"),
}).strict();

export const placeSearchHubs = [
  { name:"강남역", lat:37.498, lon:127.028 },
  { name:"홍대입구", lat:37.557, lon:126.925 },
  { name:"을지로3가", lat:37.566, lon:126.992 },
  { name:"성수", lat:37.545, lon:127.056 },
  { name:"건대입구", lat:37.54, lon:127.07 },
  { name:"부산 서면", lat:35.157, lon:129.059 },
  { name:"부산 해운대", lat:35.164, lon:129.158 },
  { name:"대구 동성로", lat:35.869, lon:128.595 },
  { name:"대전 둔산", lat:36.352, lon:127.378 },
  { name:"광주 상무", lat:35.154, lon:126.849 },
  { name:"제주 시청", lat:33.5, lon:126.532 },
] as const;

export type PlaceSearchHub = (typeof placeSearchHubs)[number];

export function nearestPlaceSearchHub(latitude:number, longitude:number):PlaceSearchHub {
  return placeSearchHubs.reduce((nearest, hub) => {
    const distance = (hub.lat - latitude) ** 2 + (hub.lon - longitude) ** 2;
    const nearestDistance = (nearest.lat - latitude) ** 2 + (nearest.lon - longitude) ** 2;
    return distance < nearestDistance ? hub : nearest;
  });
}

const publicRoadAddress = (value: string) => {
  const compact = value.normalize("NFKC").replace(/\s+/g, " ").trim();
  const province = /(?:서울(?:특별시)?|부산(?:광역시)?|대구(?:광역시)?|인천(?:광역시)?|광주(?:광역시)?|대전(?:광역시)?|울산(?:광역시)?|세종(?:특별자치시)?|경기(?:도)?|강원(?:특별자치도|도)?|충청[남북]도|전라[남북](?:특별자치)?도|경상[남북]도|제주(?:특별자치도)?)/u;
  const locality = /(?:^|\s)(?!(?:서울특별시|부산광역시|대구광역시|인천광역시|광주광역시|대전광역시|울산광역시|세종특별자치시|경기도|강원도|강원특별자치도|충청북도|충청남도|전라북도|전북특별자치도|전라남도|경상북도|경상남도|제주도|제주특별자치도)(?:\s|$))[가-힣]{1,12}(?:시|군|구|읍|면|동)(?=\s|$)/u;
  const roadAndBuilding = /(?:대로|로|길)\s*(?:지하\s*)?\d{1,5}(?:-\d{1,4})?(?:번지)?(?:\s|$)/u;
  return province.test(compact) && locality.test(compact) && roadAndBuilding.test(compact);
};

export const officialLicenseLookupSchema = z.object({
  venueName: z.string().trim().min(2).max(80).refine((value) => !/[\u0000-\u001F\u007F]/u.test(value)),
  roadAddress: z.string().trim().min(10).max(180).refine(publicRoadAddress, "전체 도로명 주소가 필요합니다."),
}).strict();
export type VenueIntent = z.infer<typeof venueIntentSchema>;

export type VenuePhase = "tonight" | "tomorrow";
export type VenueIntentGroup = "occasion" | "place" | "food" | "soup" | "meal";
export type VenueIntentMeta = {
  label: string;
  icon: string;
  query: string;
  description: string;
  phases: readonly VenuePhase[];
  group: VenueIntentGroup;
  tomorrowGroup?: VenueIntentGroup;
};

export const venueIntentMeta: Record<VenueIntent, VenueIntentMeta> = {
  local_pub:{ label:"근처 술집", icon:"🍻", query:"술집", description:"주점·펍 업종부터 넓게", phases:["tonight"], group:"occasion" },
  first_round:{ label:"1차·식사", icon:"🍚", query:"1차 술집", description:"식사부터 시작하기 좋은 곳", phases:["tonight"], group:"occasion" },
  second_round:{ label:"2차·가볍게", icon:"🥂", query:"2차 술집", description:"주점·바 업종 중심", phases:["tonight"], group:"occasion" },
  group:{ label:"회식·단체", icon:"👥", query:"회식 단체 모임", description:"예약·수용 인원 단서가 있는 곳", phases:["tonight"], group:"occasion" },
  date:{ label:"둘이 대화", icon:"💬", query:"데이트 조용한 술집", description:"데이트·대화 검색 표현", phases:["tonight"], group:"occasion" },
  solo:{ label:"혼술", icon:"🪑", query:"혼술", description:"혼자 한잔 검색 표현", phases:["tonight"], group:"occasion" },
  outdoor:{ label:"야장·테라스", icon:"🌙", query:"야장 테라스 술집", description:"야외 좌석 단서가 있는 곳", phases:["tonight"], group:"occasion" },
  late_night:{ label:"늦게까지", icon:"🕛", query:"심야 술집", description:"늦은 영업시간 단서가 있는 곳", phases:["tonight"], group:"occasion" },
  korean_pub:{ label:"한식주점·전통주", icon:"🍶", query:"한식주점 전통주", description:"막걸리·전통주·한식주점", phases:["tonight"], group:"place" },
  pocha:{ label:"포차·전집", icon:"🏮", query:"포차 전집", description:"포장마차·전·빈대떡", phases:["tonight"], group:"place" },
  old_school:{ label:"오래된 노포", icon:"🕰️", query:"오래된 노포", description:"오래된 연도 단서가 있는 곳", phases:["tonight"], group:"place" },
  izakaya:{ label:"이자카야·사케", icon:"🍢", query:"이자카야 사케", description:"이자카야·야키토리·사케", phases:["tonight"], group:"place" },
  wine_bar:{ label:"와인바", icon:"🍷", query:"와인바", description:"와인·보틀숍·와인바", phases:["tonight"], group:"place" },
  cocktail_bar:{ label:"하이볼·칵테일", icon:"🍸", query:"하이볼 칵테일바", description:"하이볼·칵테일·바", phases:["tonight"], group:"place" },
  craft_beer:{ label:"수제맥주", icon:"🍺", query:"수제맥주 브루어리", description:"브루어리·탭룸·수제맥주", phases:["tonight"], group:"place" },
  meat:{ label:"고기·구이", icon:"🥩", query:"고기 구이 술집", description:"삼겹살·바비큐·구이", phases:["tonight"], group:"food" },
  gopchang:{ label:"곱창·내장", icon:"🔥", query:"곱창 막창 대창", description:"곱창·막창·대창·내장", phases:["tonight"], group:"food" },
  seafood:{ label:"회·해산물", icon:"🐟", query:"회 해산물 술집", description:"회·조개·해산물", phases:["tonight"], group:"food" },
  spicy:{ label:"매운 안주", icon:"🌶️", query:"매운 안주", description:"닭발·떡볶이·마라", phases:["tonight"], group:"food" },
  soup:{ label:"국물 안주", icon:"🍲", query:"국물 안주", description:"전골·탕·찌개", phases:["tonight"], group:"food" },
  chinese:{ label:"중식 안주", icon:"🥟", query:"중식 술집", description:"중식·중화요리", phases:["tonight", "tomorrow"], group:"food", tomorrowGroup:"meal" },
  japanese:{ label:"일식", icon:"🍣", query:"일식", description:"스시·라멘·일식", phases:["tonight", "tomorrow"], group:"food", tomorrowGroup:"meal" },
  fried_chicken:{ label:"치킨·튀김", icon:"🍗", query:"치킨 튀김 술집", description:"치킨·가라아게·튀김", phases:["tonight"], group:"food" },
  pizza:{ label:"피자", icon:"🍕", query:"피자", description:"피자·피제리아", phases:["tonight", "tomorrow"], group:"food", tomorrowGroup:"meal" },
  cheese:{ label:"치즈 안주", icon:"🧀", query:"치즈 안주", description:"치즈 메뉴", phases:["tonight"], group:"food" },
  clear_soup:{ label:"맑은 국물", icon:"🥣", query:"맑은 국물", description:"곰탕·설렁탕·복국", phases:["tomorrow"], group:"soup" },
  spicy_soup:{ label:"얼큰한 국물", icon:"🌶️", query:"매운 국물", description:"짬뽕·육개장·매운탕", phases:["tomorrow"], group:"soup" },
  gukbap:{ label:"국밥·탕", icon:"🍚", query:"국밥 탕", description:"국밥·감자탕·갈비탕", phases:["tomorrow"], group:"soup" },
  bean_sprout:{ label:"콩나물·북엇국", icon:"🌱", query:"콩나물국 북엇국", description:"콩나물국·북엇국·황태국", phases:["tomorrow"], group:"soup" },
  cold_noodle:{ label:"냉면·밀면", icon:"🧊", query:"냉면 밀면", description:"차갑고 산뜻한 면", phases:["tomorrow"], group:"meal" },
  noodle:{ label:"쌀국수·우동", icon:"🍜", query:"쌀국수 우동", description:"쌀국수·우동·국수", phases:["tomorrow"], group:"meal" },
  porridge:{ label:"죽·가벼운 밥", icon:"🥄", query:"죽 가벼운 식사", description:"죽·오트밀·부드러운 밥", phases:["tomorrow"], group:"meal" },
  burger:{ label:"햄버거", icon:"🍔", query:"햄버거", description:"버거·수제버거", phases:["tomorrow"], group:"meal" },
};

export function venueIntentGroupFor(intent: VenueIntent, phase: VenuePhase) {
  const meta = venueIntentMeta[intent];
  return phase === "tomorrow" ? meta.tomorrowGroup ?? meta.group : meta.group;
}

export const lastTrainQuerySchema = z.object({
  stationCode: z.string().regex(/^\d{4}$/),
  weekTag: z.coerce.number().int().min(1).max(3),
}).strict();

export type OfficialLicenseStatus = {
  status: "open" | "closed" | "unknown";
  statusLabel: string;
  permitDate: string | null;
  updatedAt: string | null;
  provider: "행정안전부 지방행정 인허가";
};

export type OfficialLicenseLookupResult =
  | {
    status: "official";
    checkedAt: string;
    source: { provider: "행정안전부 지방행정 인허가"; license: "이용범위 제한 없음" };
    official: OfficialLicenseStatus;
  }
  | {
    status: "unconfirmed";
    checkedAt: string;
    source: { provider: "행정안전부 지방행정 인허가"; license: "이용범위 제한 없음" };
    reason: "not_matched" | "ambiguous" | "provider_unavailable" | "not_configured";
  };

export type VenueCandidate = {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  distanceMeters: number;
  category: string;
  address: string | null;
  whyMatched: string;
  confidence: "strong" | "partial" | "nearby";
  evidence: string[];
  missingEvidence: string[];
  provider: "Kakao Local" | "OpenStreetMap";
  phone: string | null;
  website: string | null;
  openingHours: string | null;
  detailUrl: string;
  directionsUrl: string;
  official: OfficialLicenseStatus | null;
};

export type PlaceSearchResult = {
  candidates: VenueCandidate[];
  nearbyCandidates: VenueCandidate[];
  recovery: null | { intent: VenueIntent; label: string; candidates: VenueCandidate[] };
  intent: VenueIntent;
  searchedCenter: { latitude: number; longitude: number; precisionMeters: number };
  radiusMeters: number;
  quality: { qualifiedCount: number; nearbyCount: number; excludedCount: number };
  source: {
    providers: Array<{ name: "Kakao Local" | "OpenStreetMap Overpass" | "OpenStreetMap via ohsome" | "행정안전부 지방행정 인허가"; status: "live" | "snapshot" | "unavailable"; instance?: string; license: string; checkedAt: string }>;
    snapshotAt: string | null;
    checkedAt: string;
    freshness: "live" | "stale";
  };
  limitations: string[];
};

export type LastTrainOption = {
  stationCode: string;
  stationName: string;
  line: string;
  directionCode: "1" | "2";
  directionLabel: string;
  destination: string;
  departureTime: string;
  rawDepartureTime: string;
};

export type PopularStationLine = {
  stationCode: string;
  stationName: string;
  line: string;
  hub: string;
};

export type LastTrainResult = {
  options: LastTrainOption[];
  source: {
    provider: "서울교통공사";
    mode: "live" | "snapshot";
    checkedAt: string;
    datasetUpdatedAt: string;
    license: "공공누리 1유형";
  };
  limitations: string[];
};

export const bootstrapQuerySchema = z.object({ channel: channelSchema.default("web"), build: z.string().min(1).max(80).default("local") });
export const createRoomSchema = z.object({
  partySize: z.number().int().min(2).max(20),
  selectedProps: z.array(z.string().min(1).max(30)).max(8).default([]),
  catalogVersion: z.string().min(1).max(40),
}).strict();
export const joinRoomSchema = z.object({ code: z.string().regex(/^[0-9A-HJKMNP-TV-Z]{6}$/), nickname: z.string().trim().min(1).max(24) }).strict();
export const roomEventSchema = z.object({
  eventId: z.string().uuid(),
  expectedRevision: z.number().int().nonnegative(),
  type: z.enum(["PARTICIPANT_READY", "QUEUE_UPDATED", "ROUND_STARTED", "ROUND_COMPLETED", "ROUND_SKIPPED", "REST_DEFERRED", "REST_COMPLETED", "SESSION_ENDED"]),
  payload: z.record(z.string(), z.unknown()).default({}).refine(
    (payload) => Object.keys(payload).every((key) => !/answer|nickname|contact|phone|email|drink|amount|bac|roomcode|photo/i.test(key)),
    "sensitive room payload",
  ),
}).strict();

export const entitlementProviderStateSchema = z.enum(["unavailable", "pending", "active", "refunded", "charged_back", "expired"]);
export const commerceProviderSchema = z.enum(["web_pg", "apps_in_toss", "app_store", "google_play"]);
export const commerceSkuSchema = z.enum(["host_lifetime_candidate", "theme_candidate"]);
export const purchaseVerifySchema = z.object({ provider:commerceProviderSchema, orderReference:z.string().min(8).max(512), sku:commerceSkuSchema }).strict();
export const purchaseRestoreSchema = z.object({ provider:commerceProviderSchema, subjectReference:z.string().min(8).max(256) }).strict();

export const analyticsEventNames = [
  // app_open은 연령 확인 이전, 진입 즉시 1회 발화한다. 이것이 퍼널의 분모다 —
  // 나이 게이트 뒤에서만 세면 "게이트를 통과한 사람 중 비율"이 되어 언제나 좋아 보인다.
  "app_open",
  "landing_viewed", "age_gate_accepted", "condition_selected", "recommendations_shown", "recommendation_accepted",
  "catalog_searched", "random_game_drawn", "game_rule_opened", "game_started", "game_completed", "first_game_started", "first_game_completed", "participant_passed",
  "outcome_selected", "rest_card_seen", "rest_card_accepted", "rest_card_deferred", "queue_started",
  "session_completed", "recap_saved", "group_preset_saved", "preset_reopened", "room_created",
  "guest_join_succeeded", "room_reconnected", "host_transferred", "safety_block_viewed", "paywall_viewed",
  "purchase_started", "purchase_succeeded", "purchase_failed", "purchase_restored", "support_submitted",
  "privacy_delete_completed", "place_search_started", "place_search_succeeded", "place_search_empty",
  "place_search_failed", "venue_saved", "last_train_loaded", "meetup_feed_viewed", "meetup_created",
  "meetup_join_requested", "meetup_joined", "meetup_checked_in", "meetup_chat_opened", "meetup_message_sent",
  "meetup_message_blocked", "meetup_ended", "meetup_chat_closed", "meetup_reported", "meetup_reconnected",
] as const;
export const analyticsEventNameSchema = z.enum(analyticsEventNames);
const forbiddenProp = /answer|nickname|role|drink|amount|contact|roomcode|query/i;
export const analyticsEventSchema = z.object({
  id: z.string().uuid(),
  name: analyticsEventNameSchema,
  occurredAt: z.string().datetime(),
  props: z.record(z.string().max(40), z.union([z.string().max(120), z.number(), z.boolean()])).refine((props) => Object.keys(props).every((key) => !forbiddenProp.test(key)), "sensitive analytics property"),
}).strict();
export const analyticsBatchSchema = z.object({ events: z.array(analyticsEventSchema).min(1).max(20) }).strict();

export const errorCatalog = {
  REQ_400: { status: 400, message: "요청을 이해하지 못했어요. 화면을 새로 열어 주세요." },
  REQ_404: { status: 404, message: "요청한 경로를 찾을 수 없어요." },
  REQ_413: { status: 413, message: "한 번에 보낼 수 있는 크기를 넘었어요. 내용을 나눠 주세요." },
  REQ_429: { status: 429, message: "요청이 잠시 몰렸어요. 조금 뒤 다시 시도해 주세요." },
  AUTH_401: { status: 401, message: "연결이 만료됐어요. 다시 이어볼게요." },
  AUTH_403: { status: 403, message: "이 행동은 현재 진행자만 할 수 있어요." },
  AGE_403: { status: 403, message: "이 서비스는 만 19세 이상만 이용할 수 있어요." },
  CAT_404: { status: 404, message: "이 게임은 현재 원장에서 찾을 수 없어요." },
  CAT_400: { status: 400, message: "게임 조건을 다시 확인해 주세요." },
  CAT_409: { status: 409, message: "게임 원장이 바뀌었어요. 최신 목록으로 맞출게요." },
  ROOM_400: { status: 400, message: "방 설정을 다시 확인해 주세요." },
  ROOM_401: { status: 401, message: "방 연결이 만료됐어요. 다시 참여해 주세요." },
  ROOM_403: { status: 403, message: "이 방 동작을 실행할 권한이 없어요." },
  ROOM_404: { status: 404, message: "이 방은 쉬러 갔어요. 같은 설정으로 새 방을 열 수 있어요." },
  ROOM_409: { status: 409, message: "방 상태가 먼저 바뀌었어요. 최신 화면으로 맞출게요." },
  ROOM_410: { status: 410, message: "방 이용 시간이 끝났어요." },
  ROOM_429: { status: 429, message: "잠깐만 쉬었다가 다시 연결해 주세요." },
  ROOM_422: { status: 422, message: "이 방에서는 지금 그 동작을 할 수 없어요." },
  GAME_403: { status: 403, message: "이 원형 규칙은 안전상 실행하지 않아요. 안전 변형을 골라 주세요." },
  GAME_404: { status: 404, message: "진행 중인 게임을 찾지 못했어요. 순서에서 다시 골라 주세요." },
  DATA_409: { status: 409, message: "기기와 서버 상태가 달라요. 저장된 내용을 먼저 확인해 주세요." },
  DATA_503: { status: 503, message: "온라인 저장은 잠시 쉬고 있어요. 이 기기에서는 계속할 수 있어요." },
  PURCHASE_400: { status: 400, message: "구매 요청을 다시 확인해 주세요." },
  PURCHASE_402: { status: 402, message: "결제가 완료되지 않았어요. 무료 게임은 그대로 이용할 수 있어요." },
  PURCHASE_409: { status: 409, message: "구매 상태가 바뀌었어요. 복원 결과를 다시 확인해 주세요." },
  PURCHASE_503: { status: 503, message: "구매 기능을 사용할 수 없어요. 무료 게임은 그대로 이용할 수 있어요." },
  SUPPORT_400: { status: 400, message: "문의 내용을 다시 확인해 주세요." },
  SUPPORT_503: { status: 503, message: "지금은 전송할 수 없어요. 작성한 내용은 이 기기에 남겨둘게요." },
  PRIVACY_409: { status: 409, message: "삭제 상태를 다시 확인하고 있어요. 기기 데이터는 설정에서 바로 지울 수 있어요." },
  PRIVACY_503: { status: 503, message: "온라인 개인정보 요청을 보낼 수 없어요. 기기 데이터는 설정에서 바로 지울 수 있어요." },
  MEETUP_400: { status: 400, message: "모임 내용을 다시 확인해 주세요." },
  MEETUP_401: { status: 401, message: "본인확인 연결이 만료됐어요. 다시 확인해 주세요." },
  MEETUP_403: { status: 403, message: "이 모임에서 실행할 수 없는 행동이에요." },
  MEETUP_404: { status: 404, message: "이 모임을 찾을 수 없어요." },
  MEETUP_409: { status: 409, message: "모임 상태가 먼저 바뀌었어요. 최신 화면으로 맞출게요." },
  MEETUP_410: { status: 410, message: "이 모임의 이용 시간이 끝났어요." },
  MEETUP_422: { status: 422, message: "공개 모임 안전 기준에 맞지 않는 내용이 있어요." },
  MEETUP_429: { status: 429, message: "모임 요청이 너무 빨라요. 잠시 뒤 다시 시도해 주세요." },
  MEETUP_503: { status: 503, message: "모임 모집은 본인확인과 안전 운영 연결을 준비하고 있어요. 장소와 게임 도구는 계속 쓸 수 있어요." },
  MESSAGE_400: { status: 400, message: "메시지를 다시 확인해 주세요." },
  MESSAGE_403: { status: 403, message: "참여가 확정된 사람만 이 그룹채팅을 사용할 수 있어요." },
  MESSAGE_410: { status: 410, message: "모임 종료 후 3시간이 지나 메시지 전송이 끝났어요." },
  MESSAGE_422: { status: 422, message: "연락처·외부 유도·불법 또는 불쾌한 표현은 보낼 수 없어요." },
  MESSAGE_429: { status: 429, message: "메시지를 너무 빠르게 보내고 있어요. 잠깐만 기다려 주세요." },
  MODERATION_403: { status: 403, message: "운영자 확인 권한이 없어요." },
  MODERATION_409: { status: 409, message: "신고 상태가 먼저 바뀌었어요." },
  MODERATION_503: { status: 503, message: "안전 운영 큐를 사용할 수 없어 모임 기능을 잠시 닫았어요." },
} as const;
export type ErrorCode = keyof typeof errorCatalog;

export function apiOk<T>(data: T, requestId = crypto.randomUUID()) {
  return { ok: true as const, data, requestId, serverTime: new Date().toISOString() };
}
export function apiError(code: ErrorCode, requestId = crypto.randomUUID()) {
  const error = errorCatalog[code];
  return { ok: false as const, error: { code, message: error.message, retryable: [409, 429, 503].includes(error.status) }, requestId };
}

export * from "./meetup";
